import { useEffect, useMemo, useRef, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import { ActionIcon, Loader, Menu, TextInput, Tooltip } from '@mantine/core'

import { useQuery } from '@redwoodjs/web'

import StyledMenu from 'src/components/Overlays/StyledMenu/StyledMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_NOTE_TEMPLATES,
  RESOLVE_NOTE_TEMPLATE,
} from 'src/graphql/noteTemplates.graphql'
import IconAdd from 'src/icons/IconAdd'
import IconAlertCircle from 'src/icons/IconAlertCircle'
import IconSearch from 'src/icons/IconSearch'
import { NOTE_TEMPLATE_PAGE_LIMIT } from 'src/lib/notes'

interface NoteTemplateMenuProps {
  applicantId: string
  onTemplateSelect: (content: string) => void
}

/** Templates load on first open and are filtered client-side, like the SMS picker. */
const NoteTemplateMenu = ({
  applicantId,
  onTemplateSelect,
}: NoteTemplateMenuProps) => {
  const [opened, setOpened] = useState(false)
  const [hasOpened, setHasOpened] = useState(false)
  const [search, setSearch] = useState('')

  // The composer stays mounted when the drawer moves to another applicant,
  // so a resolve that was in flight must not land in the new applicant's note.
  const applicantIdRef = useRef(applicantId)
  useEffect(() => {
    applicantIdRef.current = applicantId
  }, [applicantId])
  const requestedApplicantIdRef = useRef<string | null>(null)

  // cache-and-network: templates edited in Settings (a different route, so
  // refetchQueries cannot reach this inactive query) show up on next open,
  // and a failed fetch is retried instead of leaving the error sticky.
  const { data, loading, error } = useQuery(GET_NOTE_TEMPLATES, {
    variables: {
      pageInfo: { limit: NOTE_TEMPLATE_PAGE_LIMIT, offset: 0 },
      orderBy: { field: 'title', direction: 'asc' },
    },
    skip: !hasOpened,
    fetchPolicy: 'cache-and-network',
  })

  const [resolveNoteTemplate, { loading: resolving }] = useLazyQuery(
    RESOLVE_NOTE_TEMPLATE,
    {
      fetchPolicy: 'network-only',
      onCompleted: (results) => {
        if (requestedApplicantIdRef.current !== applicantIdRef.current) {
          return
        }
        onTemplateSelect(results.resolveNoteTemplate.content)
        setOpened(false)
      },
      onError: (error) => {
        const errorMessage =
          error.graphQLErrors?.[0]?.extensions?.originalError?.message ||
          error.graphQLErrors?.[0]?.message ||
          error.message ||
          'Failed to resolve template'
        toast(errorMessage, 'error')
      },
    }
  )

  const templates = useMemo(() => {
    const items = data?.noteTemplates?.items ?? []
    const term = search.trim().toLowerCase()
    if (!term) return items
    return items.filter((item) => item.title.toLowerCase().includes(term))
  }, [data, search])

  const renderItems = () => {
    if (loading) {
      return (
        <Menu.Item>
          <span className="flex flex-row items-center">
            <Loader size={16} />
            &nbsp; Loading...
          </span>
        </Menu.Item>
      )
    }

    if (error) {
      return (
        <Menu.Item>
          <span className="flex flex-row items-center text-doubleNickel-error-500">
            <IconAlertCircle className="fill-none stroke-doubleNickel-error-500" />
            &nbsp; Something went wrong
          </span>
        </Menu.Item>
      )
    }

    if (templates.length === 0) {
      return (
        <Menu.Item className="text-xs">
          {search.trim() ? 'No templates found' : 'No note templates yet'}
        </Menu.Item>
      )
    }

    return templates.map((item) => (
      <Menu.Item
        key={item.noteTemplateId}
        title={item.title}
        className="text-xs"
        closeMenuOnClick={false}
        disabled={resolving}
        onClick={() => {
          requestedApplicantIdRef.current = applicantId
          resolveNoteTemplate({
            variables: { noteTemplateId: item.noteTemplateId, applicantId },
          })
        }}
      >
        {item.title}
      </Menu.Item>
    ))
  }

  return (
    <StyledMenu
      width={300}
      position="top-start"
      middlewares={{ flip: false, shift: true }}
      opened={opened}
      onChange={(next: boolean) => {
        setOpened(next)
        if (next) setHasOpened(true)
        if (!next) setSearch('')
      }}
    >
      <Menu.Target>
        <Tooltip label="Add template" position="top" className="text-xs">
          <ActionIcon
            variant="subtle"
            radius="md"
            aria-label="Add template"
            // Don't let the click itself move focus out of the composer,
            // matching the emoji picker trigger.
            onMouseDown={(event) => event.preventDefault()}
            className="transition-colors hover:bg-doubleNickel-gray-100"
          >
            <IconAdd className="fill-none stroke-doubleNickel-gray-500" />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        <div className="flex max-h-[350px] flex-col">
          <div className="shrink-0 bg-white px-2 pb-1">
            <TextInput
              placeholder="Search templates..."
              size="xs"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              // Keep arrow and letter keys in the search box instead of
              // letting the menu's own keyboard navigation consume them.
              onKeyDown={(e) => e.stopPropagation()}
              leftSection={
                <IconSearch className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-400" />
              }
              data-autofocus
            />
          </div>
          <div className="min-h-0 overflow-y-auto">{renderItems()}</div>
        </div>
      </Menu.Dropdown>
    </StyledMenu>
  )
}

export default NoteTemplateMenu
